import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Phone, Gift, ShieldCheck, Clock, Car } from 'lucide-react';
import { amcPlan } from '../mock';
import { Button } from '../components/ui/button';

const AMCPackages = () => {
  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-2 bg-red-600/20 backdrop-blur-sm border border-red-500/30 rounded-full text-red-400 text-sm font-medium mb-6">
              Worry-Free Car Ownership
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Carz Street Annual
              <br />
              <span className="bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">
                Maintenance Contract (AMC)
              </span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              A comprehensive programme that covers all your periodic maintenance, so you drive worry-free all year round.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Plan Overview */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-80 h-80 bg-red-600/20 rounded-full blur-3xl -mr-20 -mt-20" />

            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center">
                    <ShieldCheck className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-red-400 font-semibold uppercase tracking-wider text-sm">AMC Programme</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{amcPlan.name}</h2>
                <div className="space-y-3 text-gray-300">
                  <div className="flex items-center gap-3">
                    <Car className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <span>For <span className="text-white font-semibold">{amcPlan.forModels}</span></span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <span><span className="text-white font-semibold">{amcPlan.duration}</span> {amcPlan.durationNote}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8 text-center">
                <div className="text-gray-400 line-through text-xl mb-1">Worth &#8377;{amcPlan.actualValue}</div>
                <div className="text-gray-300 text-sm uppercase tracking-wider mb-2">Special Price For You</div>
                <div className="text-5xl md:text-6xl font-bold text-white mb-1">&#8377;{amcPlan.specialPrice}</div>
                <div className="text-red-400 text-sm mb-6">{amcPlan.priceNote}</div>
                <a href={`tel:+91${amcPlan.contact}`} className="block">
                  <Button size="lg" className="w-full bg-red-600 hover:bg-red-700 text-white">
                    <Phone className="mr-2 w-5 h-5" />
                    Book Now
                  </Button>
                </a>
                <a
                  href={`https://wa.me/91${amcPlan.contact}?text=Hi%20Carz%20Street!%20I%20want%20to%20know%20more%20about%20the%20AMC%20Comprehensive%20Maintenance%20Programme.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-3"
                >
                  <Button variant="outline" size="lg" className="w-full border-2 border-white/30 text-white hover:bg-white hover:text-gray-900">
                    Know More
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Value Benefits Table */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16"
          >
            <div className="text-center mb-10">
              <h3 className="text-3xl font-bold text-gray-900 mb-2">What's Included</h3>
              <p className="text-gray-600">Total value benefits worth &#8377;{amcPlan.actualValue}</p>
            </div>

            <div className="overflow-hidden rounded-3xl border border-gray-200 shadow-lg">
              {/* Header row */}
              <div className="hidden md:grid grid-cols-12 bg-gray-900 text-white text-sm font-semibold uppercase tracking-wider">
                <div className="col-span-7 px-6 py-4">Value Benefit</div>
                <div className="col-span-3 px-6 py-4 text-center">Frequency</div>
                <div className="col-span-2 px-6 py-4 text-right">Value (&#8377;)</div>
              </div>

              {amcPlan.benefits.map((item, index) => (
                <motion.div
                  key={index}
                  className={`grid grid-cols-1 md:grid-cols-12 items-center ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-red-50 transition-colors duration-200`}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <div className="md:col-span-7 px-6 py-4 flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-800 font-medium">{item.benefit}</span>
                  </div>
                  <div className="md:col-span-3 px-6 pb-4 md:py-4 md:text-center">
                    <span className="md:hidden text-xs text-gray-500 uppercase mr-2">Frequency:</span>
                    <span className="text-gray-700 font-semibold">{item.frequency}</span>
                  </div>
                  <div className="md:col-span-2 px-6 pb-4 md:py-4 md:text-right">
                    <span className="md:hidden text-xs text-gray-500 uppercase mr-2">Value:</span>
                    <span className="text-red-600 font-bold">&#8377;{item.value}</span>
                  </div>
                </motion.div>
              ))}

              {/* Total row */}
              <div className="grid grid-cols-1 md:grid-cols-12 items-center bg-gray-900 text-white">
                <div className="md:col-span-10 px-6 py-5 font-bold text-lg">Total Value</div>
                <div className="md:col-span-2 px-6 py-5 md:text-right">
                  <span className="text-2xl font-bold text-red-400">&#8377;{amcPlan.actualValue}</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Special Offers */}
          <div className="grid md:grid-cols-2 gap-8 mt-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-br from-red-600 to-red-700 rounded-3xl p-8 text-white"
            >
              <Gift className="w-10 h-10 mb-4" />
              <h3 className="text-2xl font-bold mb-6">Exclusive Offers</h3>
              <ul className="space-y-4">
                {amcPlan.offers.map((offer, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span className="text-white/95">{offer}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-gray-50 rounded-3xl p-8 border border-gray-200"
            >
              <ShieldCheck className="w-10 h-10 mb-4 text-red-600" />
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Terms &amp; Coverage</h3>
              <ul className="space-y-4">
                {amcPlan.terms.map((term, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-600 flex-shrink-0 mt-2.5" />
                    <span className="text-gray-700">{term}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Help strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 bg-gray-900 rounded-3xl p-8 flex flex-col md:flex-row items-center justify-between gap-6"
          >
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Ready to enroll your car?</h3>
              <p className="text-gray-400">Call our Service Advisor to get started with your AMC today.</p>
            </div>
            <a href={`tel:+91${amcPlan.contact}`}>
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white whitespace-nowrap">
                <Phone className="mr-2 w-5 h-5" />
                Call +91 {amcPlan.contact}
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AMCPackages;
